import { Box, Drawer, Typography, IconButton } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateOrUpdateKnowledgeBase, useGetKnowledgeBase } from 'apis/business/setting/knowledgeBase/hook';
import KnowledgeBaseFormSkeleton from './KnowledgeBaseFormSkeleton';
import { knowledgeBaseSchema } from 'utils/schema/validator/KnowledgeBaseValidation';
import { zodResolver } from '@hookform/resolvers/zod';
import { IoClose } from 'react-icons/io5';

export default function KnowledgeBaseForm() {
  const navigate = useNavigate();
  const { knowledgeBaseId } = useParams();
  const { data: knowledgeBase, isLoading } = useGetKnowledgeBase();
  const { handleSubmit, control } = useForm({
    resolver: zodResolver(knowledgeBaseSchema),
    values: { knowledgebase: knowledgeBase }
  });

  const { mutate: handleKnowledgeBase, isPending } = useCreateOrUpdateKnowledgeBase();

  const onSubmit = (data: any) => {
    handleKnowledgeBase(data);
  };

  if (isLoading) {
    return <KnowledgeBaseFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>{knowledgeBaseId ? 'Edit' : 'Create'} Knowledge Base</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          <Input name='knowledgebase.name' label='Name' control={control} fullWidth required />
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{ border: '1px solid #F3F3F3' }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            {knowledgeBaseId ? 'update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
